import {
  BellIcon,
  BellSlashIcon,
  PaperPlaneTiltIcon,
} from '@phosphor-icons/react';
import { SettingsContent } from '@/content/SettingsContent';
import type { NotificationSettingsProps } from '@/lib/types/notifications.types';
import { PreferenceToggle } from '@/components/ui/preference-toggle';

/**
 * Presentational component for notification settings.
 * Displays push notification enable button or preference toggles.
 */
export function NotificationSettings({
  pushEnabled,
  checkingPermission,
  preferences,
  isUpdating,
  testState,
  countdown,
  onEnablePush,
  onTogglePreference,
  onSendTestNotification,
}: NotificationSettingsProps) {
  const content = SettingsContent.notifications;

  const getTestButtonText = () => {
    const testContent = content.testNotification;
    switch (testState) {
      case 'countdown':
        return testContent.buttonSending.replace(
          '{seconds}',
          String(countdown)
        );
      case 'sending':
        return 'Sending...';
      case 'sent':
        return testContent.buttonSent;
      case 'error':
        return 'Failed - Try Again';
      default:
        return testContent.button;
    }
  };

  return (
    <section className="bg-slate-700 rounded-2xl mb-6">
      <div className="flex items-center gap-3 mb-2">
        {pushEnabled ? (
          <BellIcon size={24} weight="fill" className="text-purple-300" />
        ) : (
          <BellSlashIcon size={24} weight="fill" className="text-slate-400" />
        )}
        <h2 className="font-field-54 text-h2 text-purple-300">
          {content.title}
        </h2>
      </div>
      <p className="text-body text-slate-300 mb-6">{content.description}</p>

      {checkingPermission ? (
        <div className="animate-pulse h-12 bg-slate-600 rounded-lg" />
      ) : !pushEnabled ? (
        <button
          onClick={onEnablePush}
          className="w-full bg-slate-100 text-slate-700 rounded-lg py-3 px-6 text-body hover:bg-slate-300 transition-colors"
          aria-label={content.enableButtonAriaLabel}
        >
          {content.enableButton}
        </button>
      ) : (
        <div className="space-y-4">
          {/* Event Reminders Toggle */}
          <PreferenceToggle
            label={content.preferences.eventReminders.label}
            description={content.preferences.eventReminders.description}
            checked={preferences.notif_event_reminders}
            disabled={isUpdating || !pushEnabled}
            onChange={() => onTogglePreference('notif_event_reminders')}
          />

          {/* Event Changes Toggle */}
          <PreferenceToggle
            label={content.preferences.eventChanges.label}
            description={content.preferences.eventChanges.description}
            checked={preferences.notif_event_changes}
            disabled={isUpdating || !pushEnabled}
            onChange={() => onTogglePreference('notif_event_changes')}
          />

          <PreferenceToggle
            label={content.preferences.communityComments.label}
            description={content.preferences.communityComments.description}
            checked={preferences.notif_community_comments}
            disabled={isUpdating || !pushEnabled}
            onChange={() => onTogglePreference('notif_community_comments')}
          />

          {/* Test Notification Button */}
          <div className="pt-4 mt-4 border-t border-slate-600">
            <p className="text-small text-slate-400 mb-3">
              {content.testNotification.description}
            </p>
            <button
              onClick={onSendTestNotification}
              disabled={testState !== 'idle' && testState !== 'error'}
              aria-label={content.testNotification.ariaLabel}
              className={`flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg text-body font-field-62 transition-colors ${
                testState === 'sent'
                  ? 'bg-green-600 text-white'
                  : testState === 'error'
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : testState === 'idle'
                      ? 'bg-slate-600 text-slate-200 hover:bg-slate-500'
                      : 'bg-slate-600 text-slate-400 cursor-wait'
              }`}
            >
              <PaperPlaneTiltIcon
                size={18}
                weight="fill"
                className={
                  testState === 'sending' || testState === 'countdown'
                    ? 'animate-pulse'
                    : ''
                }
              />
              {getTestButtonText()}
            </button>
            {(testState === 'sent' || testState === 'error') && (
              <p className="text-small text-slate-400 mb-3">
                {content.testNotification.askAdminIfNoNotificationReceived}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
